import React, { useState, useMemo } from "react";
import { Box, Chip, Button, Typography, Stack, IconButton, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

import { HEALTH_STATUS_COLORS, SP_VISIT_COLORS, getSpVisitAge } from "../../constants/clinicalDashboard";
import BHStatusChip from "./BHStatusChip";
import TableSearchBar from "../CareManager/TableSearchBar";
import { useObservationsModal } from "../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../components/Advanced/DataGrid/observationsColumn";

const SEVERITY_FILTER = ["All", "Severely Complex", "Complex SPMI", "Complex Any BH", "Acute"];

/**
 * Tabla de pacientes BH sin Care Management activo.
 * Filtrada a pacientes de alta severidad + columnas productividad.
 *
 * @param {{ patients: Array, onCreateTask: (row) => void }} props
 */
export default function UnmanagedBHTable({ patients = [], onCreateTask }) {
  const navigate = useNavigate();
  const { showObservationsModal } = useObservationsModal();
  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState("All");

  const filteredRows = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return patients.filter((row) => {
      if (lowerSearch && !row.name?.toLowerCase().includes(lowerSearch)) return false;
      if (severityFilter !== "All") {
        const map = {
          "Severely Complex": "SEVERELY COMPLEX",
          "Complex SPMI": "COMPLEX CHRONIC - SPMI",
          "Complex Any BH": "COMPLEX CHRONIC - ANY BH",
          "Acute": "ACUTE",
        };
        if (row.bhStatus !== map[severityFilter]) return false;
      }
      return true;
    });
  }, [patients, search, severityFilter]);

  const columns = [
    {
      field: "name",
      headerName: "Patient",
      flex: 1,
      minWidth: 160,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={600} noWrap>{value}</Typography>
      ),
    },
    { field: "age", headerName: "Age", width: 55, align: "center", headerAlign: "center" },
    { field: "gender", headerName: "Sex", width: 50, align: "center", headerAlign: "center" },
    {
      field: "bhStatus",
      headerName: "BH Status",
      width: 170,
      renderCell: ({ value }) => <BHStatusChip status={value} />,
    },
    {
      field: "healthStatus",
      headerName: "Health Status",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const palette = HEALTH_STATUS_COLORS[value] || HEALTH_STATUS_COLORS["No Status"];
        return (
          <Chip label={value || "N/A"} size="small" sx={{ fontWeight: 600, color: palette.text, bgcolor: palette.bg, border: `1px solid ${palette.border}` }} />
        );
      },
    },
    {
      field: "globalIndicator",
      headerName: "GI",
      width: 55,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const v = value || 0;
        const color = v >= 6 ? "#DC2626" : v >= 4 ? "#D97706" : "#16A34A";
        return <Chip label={v} size="small" sx={{ fontWeight: 700, bgcolor: color, color: "#FFF", minWidth: 28 }} />;
      },
    },
    {
      field: "conditions",
      headerName: "Conditions",
      flex: 1,
      minWidth: 160,
      renderCell: ({ value }) => (
        <Typography variant="body2" noWrap title={value} sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "bhEREvents",
      headerName: "BH ER",
      width: 70,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => value > 0 ? (
        <Chip label={value} size="small" sx={{ fontWeight: 700, bgcolor: "#DC2626", color: "#FFF", minWidth: 28 }} />
      ) : <Box component="span" sx={{ color: "text.secondary" }}>0</Box>,
    },
    {
      field: "contact",
      headerName: "Contact",
      width: 80,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.3}>
          {row.hasPhone && <PhoneIcon sx={{ fontSize: 14, color: "#16A34A" }} />}
          {row.hasEmail && <EmailIcon sx={{ fontSize: 14, color: "#2563EB" }} />}
          {!row.hasPhone && !row.hasEmail && <Typography variant="caption" color="text.secondary">-</Typography>}
        </Stack>
      ),
    },
    {
      field: "lastSantePlusVisit",
      headerName: "SP Visit",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const age = getSpVisitAge(value);
        const colors = SP_VISIT_COLORS[age];
        if (!value) return <Chip label="-" size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
        const d = new Date(value);
        const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return <Chip label={label} size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
      },
    },
    {
      field: "observationsCount",
      headerName: "Obs",
      width: 65,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const val = value || 0;
        if (val === 0) return <Box component="span" sx={{ color: "text.secondary" }}>0</Box>;
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#DBEAFE", color: "#2563EB" }} />;
      },
    },
    {
      field: "activeTasks",
      headerName: "Tasks",
      width: 65,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const val = value || 0;
        if (val === 0) return <Box component="span" sx={{ color: "text.secondary" }}>0</Box>;
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#FEF3C7", color: "#D97706" }} />;
      },
    },
    getObservationsColumn(showObservationsModal, 4),
    {
      field: "actions",
      headerName: "Action",
      width: 145,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="View Patient">
            <IconButton size="small" onClick={() => navigate(`/patient-info/${row.cipId}`)} sx={{ color: "#6366F1" }}>
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<PersonAddIcon sx={{ fontSize: 16 }} />}
            onClick={() => onCreateTask?.(row)}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Enroll in CM
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <TableSearchBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search patient..."
        filters={[
          { key: "severity", label: "Severity", options: SEVERITY_FILTER, value: severityFilter },
        ]}
        onFilterChange={(key, value) => {
          if (key === "severity") setSeverityFilter(value);
        }}
      />
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.cipId}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        pageSizeOptions={[5, 10, 25]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          borderRadius: 2,
          border: "none",
          "& .MuiDataGrid-cell": { fontSize: "0.78rem" },
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: "0.78rem" },
        }}
      />
    </Box>
  );
}
